export interface PromptLibraryItem {
  id: string;
  title: string;
  description: string;
  promptText: string;
}

export const promptLibrary: PromptLibraryItem[] = [
  {
    id: "firebase-analytics-minimal",
    title: "Firebase Analytics (Minimal Setup)",
    description: `Sets up Firebase Analytics without gradle plugin or google-services.json. Uses direct FirebaseOptions initialization from JSON config.

**IMPORTANT:**

Before sending this prompt, you must copy-paste your complete google-services.json file content where indicated in the prompt.

To get the google-services.json file: Go to your Firebase Console → Project Settings → General tab → Under "Your apps" section, download the google-services.json file for your Android app.

Note: While google-services.json is Android-specific, this minimal setup extracts the Firebase configuration values from it and uses them to initialize Firebase for all platforms (Android, iOS, and web) without needing platform-specific config files.`,
    promptText: `Integrate Firebase Analytics only (no google-services.json, no Gradle plugin); add firebase_core + firebase_analytics; in main.dart call WidgetsFlutterBinding.ensureInitialized(); then await Firebase.initializeApp(options: FirebaseOptions(...)) using values from the JSON below; after initialization call FirebaseAnalytics.instance.setAnalyticsCollectionEnabled(true); if initialization fails catch and log error and continue to runApp; create android/app/src/main/res/values/analytics.xml with <string name="google_app_id">…</string> (and <string name="ga_app_id">…</string> if present); ensure initialization logic covers web + Android + iOS (use kIsWeb or Platform.isAndroid / Platform.isIOS) so Firebase.initializeApp runs on each; nothing else. Use this JSON for config:

=== JSON START ===
[paste google-services.json here]
=== JSON END ===`,
  },
  {
    id: "admob-consent-flow",
    title: "AdMob Consent Flow (GDPR/Privacy)",
    description: `Sets up Google AdMob consent management using google_mobile_ads package only. Handles consent UI, non-personalized ads configuration, and ensures SDK initialization only after consent is obtained. No ad placements yet.

**IMPORTANT:**

For this consent flow to work, you must configure consent settings in your AdMob account under Privacy & messaging (Privacy & Settings section).`,
    promptText: `Use only the google_mobile_ads package (do not include or reference user_messaging_platform).

On app startup:
• Call ConsentInformation.instance.requestConsentInfoUpdate() (with ConsentRequestParameters) to get the latest consent state.
• Then call ConsentForm.loadAndShowConsentFormIfRequired() (or equivalent) to show the consent UI if required.
Do not call MobileAds.instance.initialize() until the consent flow has completed (either the form was shown and responded to, or it was determined no form was required).

After the consent flow:
• If await ConsentInformation.instance.canRequestAds() returns true, call MobileAds.instance.initialize().
• If consent is not granted (or the user opted out of personalised ads), configure for non-personalised ads (e.g., AdRequest(nonPersonalizedAds: true)) before or at initialization.
Before any ad requests, always check canRequestAds().
In the AndroidManifest.xml, include the AdMob Application ID meta-tag (for example the official test ID) as required by the plugin and platform setup.

At this stage do not implement any ad placements (e.g., banners, interstitials) — focus only on the consent logic and SDK initialization.
Ensure that the consent flow is asynchronous and does not block the entire UI — allow the app's UI to load smoothly while awaiting or handling the consent result.This ensures that ads will only load after the user has either granted or declined consent, keeping your app compliant.`,
  },
  {
    id: "banner-ad-main-screen",
    title: "AdMob Banner Ad on Main Screen",
    description: `Displays an AdMob banner ad at the top of the main screen. Uses test ad unit ID for development and testing.

**IMPORTANT:**

This prompt uses test ad unit IDs. Before releasing your production build, ask Darvin to replace the test ad unit IDs with your production ad unit IDs from AdMob.`,
    promptText: `Add an AdMob banner ad at the top of the main screen.
Use the official AdMob banner ad unit test ID.
Ensure the banner is visible and properly positioned at the top of the screen without overlapping other UI elements.`,
  },
  {
    id: "interstitial-ad-game-over",
    title: "AdMob Interstitial Ad After Game Over",
    description: `Displays AdMob interstitial ad immediately after game ends, before game over popup. Uses test ad unit ID.

Note: "Game over" is used as an example placement. You can modify the prompt to show the interstitial ad at any other placement in your app (e.g., after completing a level, before navigating to a new screen, etc.).

**IMPORTANT:**

This prompt uses test ad unit IDs. Before releasing your production build, ask Darvin to replace the test ad unit IDs with your production ad unit IDs from AdMob.`,
    promptText: `Show AdMob interstitial ad immediately after the game ends, but before displaying the game over popup.
Use the official AdMob interstitial ad unit test ID.`,
  },
  // # Disabled temporarily until implementing grep and fixing issues with Flutter Web packages removal
  //   {
  //     id: "cleanup-unused-dependencies",
  //     title: "Clean Up Unused Dependencies",
  //     description: `Analyzes your entire Flutter/Dart codebase and updates pubspec.yaml to remove all unused dependencies. Keeps the Flutter SDK and only packages that are actually imported and required.

  // **What this does:**
  // - Scans all Dart files for actual import statements
  // - Identifies packages in pubspec.yaml that are never imported
  // - Removes unused dependencies while preserving required ones
  // - Outputs a valid pubspec.yaml that works with \`flutter pub get\`

  // **What this does NOT do:**
  // - Add new packages
  // - Change package versions
  // - Modify any Dart files
  // - Output anything except the updated pubspec.yaml`,
  //     promptText: `Clean up the Flutter project by analyzing the entire Dart codebase and updating pubspec.yaml to remove all unused dependencies while keeping the Flutter SDK and only packages that are actually imported and required, without adding new packages, changing versions, modifying Dart files, or outputting anything except a valid updated pubspec.yaml that works with flutter pub get.`,
  //   },
];
